
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __appWithI18n from 'next-translate/appWithI18n'
// @ts-ignore
    
import "../../.plasmic/plasmic/hamdast_khedmat/plasmic.css"; // plasmic-import: 666TdydnqTmUJpihDvjWrt/projectcss
import PendingPaymentSheet from '@/modules/booking/components/pendingPaymentSheet/pendingPaymentSheet';
import ErrorBoundary from "@/common/components/layouts/errorBoundary";
import PlusHostBootstrap from "@/common/components/layouts/plusHostBootstrap";
import { captureFirstTouchAttribution } from "@/common/analytics/attribution";
import useApplication from "@/common/hooks/useApplication";
import useCustomize from "@/common/hooks/useCustomize";
import { useNetworkStatus } from "@/common/hooks/useNetworkStatus";
import useServerQuery from "@/common/hooks/useServerQuery";
import { splunkInstance } from "@/common/services/splunk";
import Provider from "@/components/layouts/provider";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { PlasmicRootProvider } from "@plasmicapp/react-web";
import { Hydrate } from "@tanstack/react-query";
import { getCookie } from "cookies-next";
import type { AppProps as NextAppProps, NextWebVitalsMetric } from "next/app";
import getConfig from "next/config";
import { NextParsedUrlQuery } from "next/dist/server/request-meta";
import Head from "next/head";
import { NextRouter, useRouter } from "next/router";
import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import "react-photo-view/dist/react-photo-view.css";
import "../styles/globals.css";
// The per-project plasmic.css files are not imported: components use the identical
// plasmic.module.css; these are the only unhashed classes they reference. docs/performance.md §۳.۱۱
import "../styles/plasmic-project-globals.css";
// شیت گارد ویزیت آنلاین (پورت نلسون‌فرانت) — pages-router ایمپورت CSS سراسری را فقط از _app می‌پذیرد
import "../modules/doctorHome/components/onlineVisitGuardChats.css";
import GlobalContextsProvider from "../../.plasmic/plasmic/paziresh_24/PlasmicGlobalContextsProvider";
import { useUserInfoStore } from "@/modules/login/store/userInfo";
import axios from "axios";
import NavigationPerf from "@/common/components/layouts/NavigationPerf";
import BookingUxIssueRum from "@/common/components/layouts/BookingUxIssueRum";
import KeepAlivePages from "@/common/components/layouts/KeepAlivePages";
import { RismanSurveyScript } from "@/common/components/layouts/RismanSurveyScript";
import { RoutedExternalScripts } from "@/common/components/layouts/RoutedExternalScripts";
import { useNotificationPermission } from "@/common/hooks/useNotificationPermission";
import { sendWebVital } from "@/common/services/rum/webVitals";
import { setRumUser } from "@/common/services/rum/elastic/ids";
import { scheduleGozargahTokenUpgrade } from "@/modules/login/utils/gozargahTokenUpgrade";
import { growthbook, isEnabledGrowthbook } from "@/common/growthbook/client";
import { hideAppSplash } from "@/common/utils/appSplash";

/** Re-export leaf singleton — prefer `@/common/growthbook/client` for SSG-safe imports. */
export { growthbook };

const { publicRuntimeConfig } = getConfig();
function updateGrowthBookURL() {
  growthbook.setURL(window.location.href);
}
type withQueryProps = {
  query: NextParsedUrlQuery;
};
type AppProps = Omit<
  NextAppProps<withQueryProps & Record<string, unknown>>,
  "Component"
> & {
  Component: NextAppProps["Component"] & {
    getLayout?: (
      page: React.ReactElement,
      router: NextRouter
    ) => React.ReactNode;
  };
};
function MyApp(props: AppProps) {
  const { Component, pageProps, router } = props;
  useNetworkStatus();
  const isApplication = useApplication();
  const { asPath } = useRouter();
  const isLogin = useUserInfoStore(state => state.isLogin);
  const user = useUserInfoStore(state => state.info);
  const [isGtmReady, setIsGtmReady] = useState(false);
  const gtmEnabledRef = useRef(false);
  const gtmId = "GTM-P5RPLDP";
  const isGtmDisabled =
    String(publicRuntimeConfig.DESABLED_GTM).toLowerCase() === "true";
  useEffect(() => {
    void captureFirstTouchAttribution();
  }, []);
  useEffect(() => {
    if (isEnabledGrowthbook) {
      growthbook.loadFeatures({
        autoRefresh: true,
        skipCache: router.query.skipFlagsCache === "true"
      });
      growthbook.setAttributes({
        ...growthbook.getAttributes(),
        id: getCookie("terminal_id")
      });
      router.events.on("routeChangeComplete", updateGrowthBookURL);
    }
    return () => {
      if (growthbook.ready)
        router.events.off("routeChangeComplete", updateGrowthBookURL);
    };
  }, [router.query]);
  useEffect(() => {
    growthbook.setAttributes({
      ...growthbook.getAttributes(),
      url: location.href,
      host: location.host,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }, [asPath]);
  useEffect(() => {
    useCustomize.getState().setCustomize(pageProps?.themeConfing);
    useServerQuery.getState().setQueries(pageProps?.query);
  }, [pageProps?.query, pageProps?.themeConfing]);
  useEffect(() => {
    // PERF (DR-08 §۱۱): این side-effect-only import قبلاً بالای فایل و استاتیک بود، یعنی همیشه در
    // شاخه‌ی مشترکِ webpack هر صفحه bundle/execute می‌شد — حتی برای بازدیدهای غیر-PWA که
    // src/firebase/analytics.ts هیچ‌وقت واقعاً از آن استفاده نمی‌کند. دینامیک+deferred کردنش (بعد از
    // mount، نه در مسیرِ رندرِ بحرانی) هزینه‌ی main-thread را از مسیرِ بارگذاریِ اولیه‌ی هر صفحه حذف
    // می‌کند؛ گیتِ داخلیِ isPWA()/IS_FIREBASE_ENABLE در خودِ ماژول حفظ شده (رفتار عوض نشده).
    import("@/firebase/analytics");
  }, []);
  useEffect(() => {
    let off: (() => void) | undefined;
    // Dynamic so the Elastic RUM SDK stays out of the initial chunk.
    import("@/common/services/rum/elastic")
      .then(rum => {
        if (!rum.initElasticRum(router.pathname)) return;
        const onStart = () => rum.startRouteChange();
        const onDone = () => rum.endRouteChange(router.pathname);
        const onError = () => rum.endRouteChange(router.pathname, true);
        router.events.on("routeChangeStart", onStart);
        router.events.on("routeChangeComplete", onDone);
        router.events.on("routeChangeError", onError);
        off = () => {
          router.events.off("routeChangeStart", onStart);
          router.events.off("routeChangeComplete", onDone);
          router.events.off("routeChangeError", onError);
        };
      })
      .catch(() => undefined);
    return () => off?.();
  }, []);
  useEffect(() => {
    // user.id on RUM events (APM is behind SSO + IP allowlist); queued until the agent starts.
    setRumUser(isLogin ? user?.id : undefined);
  }, [isLogin, user?.id]);
  useEffect(() => {
    // /apphome hides the app splash itself once its content chunk arrives; any other page
    // the app lands on (e.g. the doctor-home redirect) is ready as soon as it mounts.
    if (router.pathname !== "/apphome") hideAppSplash();
  }, [router.pathname]);
  useEffect(() => {
    if (
      isLogin &&
      (isApplication ||
        ("Notification" in window && Notification?.permission === "granted"))
    ) {
      window.najvaUserSubscribed = function (najva_user_token: string) {
        axios.post(
          `${publicRuntimeConfig.API_GATEWAY_BASE_URL}/v1/notification/subscribers`,
          {
            user_id: user.id,
            subscriber_token: najva_user_token
          }
        );
      };
    }
  }, [isLogin, isApplication]);

  // Auto-subscribe نوتیفیکشن در صورت login و وجود دسترسی
  useNotificationPermission({
    autoSubscribe: isLogin // فقط وقتی login است auto-subscribe کند
  });

  // Silent gozargah_token RS256 refresh for sessions that still only have legacy cookies.
  useEffect(() => {
    if (!isLogin) return;
    scheduleGozargahTokenUpgrade();
  }, [isLogin]);

  useEffect(() => {
    if (isGtmDisabled || gtmEnabledRef.current) return;
    const isDoctorPage =
      router.pathname === "/dr/[slug]" || router.asPath.startsWith("/dr/");
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let idleId: any;
    const enable = () => {
      if (gtmEnabledRef.current) return;
      gtmEnabledRef.current = true;
      setIsGtmReady(true);
    };
    const onInteract = () => enable();
    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (idleId && typeof (window as any).cancelIdleCallback === "function")
        (window as any).cancelIdleCallback(idleId);
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("scroll", onInteract);
    };
    if (isDoctorPage) {
      window.addEventListener("pointerdown", onInteract, {
        passive: true,
        once: true
      });
      window.addEventListener("keydown", onInteract, {
        passive: true,
        once: true
      });
      window.addEventListener("touchstart", onInteract, {
        passive: true,
        once: true
      });
      window.addEventListener("scroll", onInteract, {
        passive: true,
        once: true
      });
      timeoutId = setTimeout(enable, 10000);
    } else {
      const ric = (window as any).requestIdleCallback as
        | undefined
        | ((
            cb: () => void,
            opts?: {
              timeout: number;
            }
          ) => any);
      if (typeof ric === "function")
        idleId = ric(enable, {
          timeout: 2000
        });
      else timeoutId = setTimeout(enable, 1500);
    }
    return cleanup;
  }, [router.pathname, router.asPath, isGtmDisabled]);
  const getLayout = Component.getLayout ?? (page => page);
  return (
    <ErrorBoundary>
      <GrowthBookProvider growthbook={growthbook}>
        <Provider pageProps={pageProps}>
          <GlobalContextsProvider>
            <PlasmicRootProvider disableLoadingBoundary>
              <NavigationPerf />
              <BookingUxIssueRum />
              <PendingPaymentSheet />
              <Head>
                <meta
                  name="viewport"
                  content={`viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=${isApplication ? "1.0" : "5.0"}`}
                />
              </Head>
              <Hydrate state={pageProps.dehydratedState}>
                <KeepAlivePages
                  router={router}
                  pageInputs={[Component, pageProps]}
                  renderPage={() => getLayout(<Component {...pageProps} />, router)}
                />
              </Hydrate>
              {!isGtmDisabled && isGtmReady && (
                <Script
                  id="gtm-script"
                  strategy="afterInteractive"
                  dangerouslySetInnerHTML={{
                    __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`
                  }}
                />
              )}
              <RismanSurveyScript />
              <RoutedExternalScripts />
              <PlusHostBootstrap />
            </PlasmicRootProvider>
          </GlobalContextsProvider>
        </Provider>
      </GrowthBookProvider>
    </ErrorBoundary>
  );
}
export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (metric.label === "custom" || !publicRuntimeConfig.IS_PRODUCTION) return;
  // DR-08 §۱۷ — تا امروز این تابع خالی بود و متریک‌های CWVِ کاربرانِ واقعی دور ریخته می‌شد.
  // مقصد و صفحاتِ مجاز در `webVitals.ts` تعریف شده (فعلاً فقط صفحاتِ جستجو).
  sendWebVital(metric);
}
const __Page_Next_Translate__ = MyApp;


// @ts-ignore
    export default __appWithI18n(__Page_Next_Translate__, {
// @ts-ignore
      ...__i18nConfig,
// @ts-ignore
      isLoader: true,
// @ts-ignore
      skipInitialProps: true,
// @ts-ignore
      loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
    });
// @ts-ignore
  